import FaIcon from '../../Components/FaIcon';
import { Notification, toaster } from 'rsuite';
import { IconNames } from '../../Assets/fontawesome/fa.names';
import { PlacementType } from 'rsuite/esm/toaster/ToastContainer';
import { ReactNode } from 'react';

export const isDev = process.env.NODE_ENV === 'development';

export const isProd = process.env.NODE_ENV === 'production';

type classIfType = { [key: string]: boolean };
export const classNames = (...entries: (string | classIfType)[]) => {
	let classes = '';
	entries.forEach(e => {
		if (typeof e === 'string') return (classes += e + ' ');
		classes += Object.entries(e || {})
			.filter(p => p[1] === true)
			.map(p => p[0])
			.join(' ');
	});
	return classes;
};

export const classes = (...entries: (string | classIfType)[]) => ({
	className: classNames(...(entries || [])),
});

export const clickOn = (selector: string) => (document.querySelector(selector) as any)?.click?.();

export const copyToClipboard = async (content: string) => {
	const type = 'text/plain';
	const blob = new Blob([content], { type: 'text/plain' });
	const data = [new ClipboardItem({ [type]: blob })];
	return await navigator.clipboard.write(data);
};

type NotifyOptions = {
	icon?: IconNames;
	duration?: number;
	className?: string;
	closable?: boolean;
	header?: string;
	placement?: PlacementType;
	type?: 'success' | 'error' | 'info' | 'warning';
};

//? One live toast per placement so rapid actions replace the previous message instead of stacking
const activeToasts = new Map<string, string>();
const pendingReplacements = new Map<string, ReturnType<typeof setTimeout>>();

//---------RsuiteNotification-------------
export const notify = (message: string | ReactNode, options?: NotifyOptions) => {
	const type = options?.type;
	const placement = options?.placement || 'bottomStart';
	const duration = options?.duration || 3000;
	let interval = setInterval(() => {
		if (!!toaster?.push) {
			clearInterval(interval);

			const pendingReplacement = pendingReplacements.get(placement);
			if (pendingReplacement) clearTimeout(pendingReplacement);

			const previousKey = activeToasts.get(placement);
			if (previousKey) {
				toaster.remove(previousKey);
				activeToasts.delete(placement);
			}

			const show = () => {
				pendingReplacements.delete(placement);
				const key = toaster.push(
					<Notification
						type={type}
						className={classNames(`rs-custom-toast ${options?.className || ''} `, {
							'bg-error': type === 'error',
							'bg-primary': type === 'info',
							'bg-success': type === 'success',
							'bg-yellow-500': type === 'warning',
						})}
						duration={duration}
						closable={options?.closable === undefined ? true : options?.closable}>
						<div className='rs-description-content'>
							{options?.icon && <FaIcon fa={options?.icon} />}
							{message}
						</div>
					</Notification>,
					{ placement }
				);
				activeToasts.set(placement, key);
				//? Forget the key once the toast has auto-dismissed
				setTimeout(() => {
					if (activeToasts.get(placement) === key) activeToasts.delete(placement);
				}, duration + 500);
			};

			if (previousKey || pendingReplacement) {
				//? RSuite cleans a removed toast up after 400ms; pushing sooner gets wiped by that cleanup
				pendingReplacements.set(placement, setTimeout(show, 450));
			} else {
				show();
			}
		}
	}, 200);
};
//

export const Notify = {
	error: (message: string | ReactNode, options?: NotifyOptions) => {
		notify(message, { type: 'error', icon: 'l-xmark', ...(options || {}) });
	},
	info: (message: string | ReactNode, options?: NotifyOptions) => {
		notify(message, { type: 'info', icon: 'l-info', ...(options || {}) });
	},
	success: (message: string | ReactNode, options?: NotifyOptions) => {
		notify(message, { type: 'success', icon: 'l-circle-check', ...(options || {}) });
	},
	warning: (message: string | ReactNode, options?: NotifyOptions) => {
		notify(message, { type: 'warning', icon: 'l-circle-exclamation', ...(options || {}) });
	},
};
